import { z } from 'zod';
import type { Signal } from '@/models/data/Signal';
import type { Rotation } from '@/models/data/Rotation';

export const PointDataSchema = z.object({ x: z.number(), y: z.number() });
export type PointDataSchema = z.infer<typeof PointDataSchema>;

// Use literal unions instead of z.nativeEnum to avoid module initialization order issues
// Signal: HIGH = 1, LOW = 0
export const SignalSchema: z.ZodType<Signal> = z.union([
  z.literal(0),
  z.literal(1),
]);
// Rotation: CW_0 = 0, CW_90 = 1, CW_180 = 2, CW_270 = 3
export const RotationSchema: z.ZodType<Rotation> = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);

export enum CircuitType {
  ChipLogic = 'ChipLogic',
  ChipStructure = 'ChipStructure',
}

export const CircuitTypeSchema = z.nativeEnum(CircuitType);

export enum Side {
  TOP = 'top',
  RIGHT = 'right',
  BOTTOM = 'bottom',
  LEFT = 'left',
}

export const SideSchema = z.nativeEnum(Side);

export const ChipIdSchema = z.string().startsWith('chip-');
export const TileIdSchema = z.string().startsWith('tile-');
export const CircuitIdSchema = z.string().startsWith('circuit-');
export const ChipInstanceIdSchema = z.string().startsWith('instance-');
export const InterfaceLinkInstanceIdSchema = z.string().startsWith('interface-link-');

export const GlobalTilePositionSchema = z.object({
  chipInstanceId: ChipInstanceIdSchema,
  location: PointDataSchema,
  circuitType: CircuitTypeSchema,
});

export const oppositeCircuitType = (circuitType: CircuitType): CircuitType => {
  return circuitType === CircuitType.ChipLogic
    ? CircuitType.ChipStructure
    : CircuitType.ChipLogic;
};

export const sideOffsets: Record<Side, { dx: number; dy: number }> = {
  [Side.TOP]: { dx: 0, dy: -1 },
  [Side.RIGHT]: { dx: 1, dy: 0 },
  [Side.BOTTOM]: { dx: 0, dy: 1 },
  [Side.LEFT]: { dx: -1, dy: 0 },
};

export const allSides = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT] as const;

export const oppositeSide = (side: Side): Side => {
  switch (side) {
    case Side.TOP:
      return Side.BOTTOM;
    case Side.RIGHT:
      return Side.LEFT;
    case Side.BOTTOM:
      return Side.TOP;
    case Side.LEFT:
      return Side.RIGHT;
  }
};
